import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import SEOHead from "@/components/base/SEOHead";
import VideoCard from "@/components/feature/VideoCard";
import { categories, videos, creators, stats } from "@/mocks/data";

function formatNumber(num: number): string {
  if (num >= 10000) return (num / 10000).toFixed(1) + "万";
  return num.toString();
}

export default function HomePage() {
  const { t } = useTranslation();

  const hotVideos = [...videos].sort((a, b) => b.views - a.views).slice(0, 8);
  const featuredCategories = categories.filter((c) => c.id !== "all").slice(0, 6);

  const homeSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "快猫视频",
    url: "https://www.kuaimaovideo.com",
    description: t("site.description"),
    potentialAction: {
      "@type": "SearchAction",
      target: "https://www.kuaimaovideo.com/search?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "快猫视频",
    url: "https://www.kuaimaovideo.com",
    description: t("site.description"),
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "400-888-9999",
      contactType: "customer service",
    },
  };

  return (
    <>
      <SEOHead
        title="短视频创作与分享平台"
        description={t("site.description")}
        keywords="快猫视频,快猫视频下载,快猫视频APP,快猫短视频平台,快猫视频创作工具,快猫视频热门内容,快猫视频官方入口"
        schema={{ ...homeSchema, ...orgSchema }}
      />

      {/* Hero Section */}
      <section className="relative w-full min-h-[600px] md:min-h-[700px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Modern%20creative%20studio%20workspace%20with%20warm%20orange%20and%20coral%20ambient%20lighting%2C%20video%20production%20equipment%2C%20soft%20gradient%20background%20with%20abstract%20geometric%20shapes%2C%20cinematic%20atmosphere%2C%20warm%20inviting%20tech%20creative%20space&width=1920&height=900&seq=hero-bg-home&orientation=landscape"
          alt="快猫视频创作平台"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
        <div className="relative z-10 container-page text-center py-20">
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading text-white mb-6 leading-tight">
            {t("home.hero.title")}
          </h1>
          <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-10 leading-relaxed">
            {t("home.hero.subtitle")}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/category"
              className="px-8 py-3.5 rounded-full bg-primary-500 text-white text-base font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              {t("home.hero.cta")}
            </Link>
            <Link
              to="/about#download"
              className="px-8 py-3.5 rounded-full bg-white/20 backdrop-blur-sm text-white border border-white/30 text-base font-medium hover:bg-white/30 transition-all whitespace-nowrap cursor-pointer"
            >
              {t("home.hero.download")}
            </Link>
          </div>

          {/* Stats */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-2xl md:text-3xl font-heading text-white">{stat.value}</div>
                <div className="text-sm text-white/60 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hot Videos Section */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="container-page">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900">
              {t("home.hotVideos")}
            </h2>
            <p className="mt-3 text-foreground-500 max-w-lg mx-auto">
              {t("home.hotVideos.desc")}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {hotVideos.map((video) => (
              <VideoCard key={video.id} {...video} />
            ))}
          </div>
        </div>
      </section>

      {/* Featured Categories */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="container-page">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900">
              {t("home.categories")}
            </h2>
            <p className="mt-3 text-foreground-500 max-w-lg mx-auto">
              {t("home.categories.desc")}
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {featuredCategories.map((cat, idx) => (
              <Link
                key={cat.id}
                to={`/category?tab=${cat.id}`}
                className={`group flex flex-col items-center gap-3 p-6 rounded-xl transition-all cursor-pointer ${
                  idx % 2 === 0
                    ? "bg-primary-50 hover:bg-primary-100"
                    : "bg-accent-50 hover:bg-accent-100"
                }`}
              >
                <div
                  className={`w-14 h-14 flex items-center justify-center rounded-full text-2xl transition-transform group-hover:scale-110 ${
                    idx % 2 === 0 ? "text-primary-500" : "text-accent-500"
                  }`}
                >
                  <i className={cat.icon}></i>
                </div>
                <span className="text-sm font-medium text-foreground-700 whitespace-nowrap">
                  {cat.name}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Trending Creators */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="container-page">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900">
              {t("home.creators")}
            </h2>
            <p className="mt-3 text-foreground-500 max-w-lg mx-auto">
              {t("home.creators.desc")}
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5">
            {creators.map((creator) => (
              <div
                key={creator.name}
                className="flex flex-col items-center gap-3 p-5 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
              >
                <img
                  src={creator.avatar}
                  alt={creator.name}
                  className="w-20 h-20 rounded-full object-cover"
                  loading="lazy"
                />
                <span className="text-sm font-medium text-foreground-900 whitespace-nowrap">
                  {creator.name}
                </span>
                <span className="text-xs text-foreground-400">
                  {creator.followers} 粉丝
                </span>
                <span className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700 whitespace-nowrap">
                  {creator.category}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Download CTA */}
      <section className="relative py-20 md:py-24 overflow-hidden" id="download">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20gradient%20background%20with%20soft%20coral%20and%20orange%20tones%2C%20abstract%20flowing%20shapes%2C%20modern%20tech%20aesthetic%2C%20mobile%20app%20showcase%20atmosphere%2C%20warm%20inviting%20digital%20creative%20space&width=1920&height=600&seq=cta-download-bg&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-primary-500/90"></div>
        <div className="relative z-10 container-page text-center">
          <h2 className="text-2xl md:text-4xl font-heading text-white mb-4">
            {t("home.download.title")}
          </h2>
          <p className="text-white/80 text-base md:text-lg mb-10 max-w-lg mx-auto">
            {t("home.download.subtitle")}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="#"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-primary-600 text-base font-semibold hover:bg-white/90 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-apple-line text-xl"></i>
              {t("home.download.ios")}
            </a>
            <a
              href="#"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white/20 backdrop-blur-sm text-white border border-white/30 text-base font-medium hover:bg-white/30 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-android-line text-xl"></i>
              {t("home.download.android")}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}